import { can, type AuthorizedUser } from '@dma/auth';
import { AuthorizationError, ValidationError, ApplicationError, type Logger } from '@dma/shared';
import { Banner, BANNER_ID } from '../../domain/entities/Banner.entity';
import { InvalidBannerError } from '../../domain/errors/InvalidBannerError';
import type { BannerRepository } from '../../domain/repositories/BannerRepository.interface';
import type { SaveBannerDto } from '../dto/SaveBannerDto';
import type { BannerDto } from '../dto/BannerDto';

/**
 * Upsert-only, matching the entity being a singleton (see BANNER_ID) — there
 * is no create-vs-update branch like SaveArticleUseCase/SaveScientificEntryUseCase
 * because there is only ever one banner row to update.
 */
export class SaveBannerUseCase {
  constructor(
    private readonly repository: BannerRepository,
    private readonly logger: Logger
  ) {}

  public async execute(actingUser: AuthorizedUser | null, input: SaveBannerDto): Promise<BannerDto> {
    if (!can(actingUser, 'settings:update')) {
      throw new AuthorizationError();
    }

    let banner: Banner;
    try {
      banner = Banner.create({
        id: BANNER_ID,
        imageUrl: input.imageUrl,
        altText: input.altText,
        isActive: input.isActive,
        updatedAt: new Date().toISOString()
      });
    } catch (error) {
      if (error instanceof InvalidBannerError) {
        throw new ValidationError(error.message);
      }
      throw error;
    }

    try {
      await this.repository.save(banner);
    } catch (error) {
      this.logger.error('Failed to persist banner', {
        cause: error instanceof Error ? error.message : String(error)
      });
      throw new ApplicationError('BANNER_SAVE_FAILED', 'ذخیره‌ی بنر با مشکل مواجه شد. لطفاً دوباره تلاش کنید.', {
        cause: error
      });
    }

    return banner.props;
  }
}
